import { useCallback, useEffect, useRef, useState } from "react";

// Each dashboard instance tracks IDs separately. No sound on the initial load.
export default function useNewOrderSound(restaurantId) {
  const contextRef = useRef(null);
  const enabledRef = useRef(false);
  const baselineRef = useRef(false);
  const seenRef = useRef(new Set());
  const [soundEnabled, setSoundEnabled] = useState(false);

  useEffect(() => {
    baselineRef.current = false;
    seenRef.current = new Set();
    enabledRef.current = false;
    setSoundEnabled(false);
    return () => {
      const context = contextRef.current;
      contextRef.current = null;
      if (context) context.close().catch(() => {});
    };
  }, [restaurantId]);

  const play = useCallback(() => {
    const context = contextRef.current;
    if (!enabledRef.current || !context || context.state !== "running") return;
    const now = context.currentTime;
    [0, 0.19, 0.38].forEach((offset) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, now + offset);
      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(20, now + offset + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 2);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(now + offset);
      oscillator.stop(now + offset + 2);
    });
  }, []);

  const enableSound = useCallback(async () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) throw new Error("Audio is not supported by this browser.");
      if (!contextRef.current) contextRef.current = new AudioContextClass();
      await contextRef.current.resume();
      enabledRef.current = contextRef.current.state === "running";
      setSoundEnabled(enabledRef.current);
      if (!enabledRef.current) throw new Error("Browser blocked sound. Click again.");
      play(); // brief confirmation chime
    } catch (error) {
      enabledRef.current = false;
      setSoundEnabled(false);
      throw error;
    }
  }, [play]);

  const disableSound = useCallback(() => {
    enabledRef.current = false;
    setSoundEnabled(false);
  }, []);

  const observeOrders = useCallback((orders) => {
    const ids = new Set(
      (Array.isArray(orders) ? orders : [])
        .map((order) => order.id ?? order.order_id)
        .filter((id) => id !== null && id !== undefined)
        .map(String)
    );
    if (!baselineRef.current) {
      baselineRef.current = true;
      ids.forEach((id) => seenRef.current.add(id));
      return;
    }
    const hasNew = [...ids].some((id) => !seenRef.current.has(id));
    ids.forEach((id) => seenRef.current.add(id));
    if (hasNew) play();
  }, [play]);

  return { soundEnabled, enableSound, disableSound, observeOrders };
}
